"use client"

import { useState, useRef } from "react"
import { Upload, X, Loader2, File, Image as ImageIcon } from "lucide-react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { storage, db } from "@/lib/firebase"
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage"
import { addDoc, collection, serverTimestamp } from "firebase/firestore"
import { toast } from "sonner"

interface UploadMediaModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
  defaultType?: "image" | "document"
}

const UploadMediaModal = ({ isOpen, onClose, onSuccess, defaultType = "image" }: UploadMediaModalProps) => {
  const [file, setFile] = useState<File | null>(null)
  const [title, setTitle] = useState("")
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const isImage = file?.type.startsWith("image/")

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (!selectedFile) return

    setFile(selectedFile)

    // Auto-fill title from filename if empty
    if (!title) {
      const rawName = selectedFile.name.split(".").slice(0, -1).join(".")
      setTitle(rawName.replace(/[-_]/g, " "))
    }

    if (selectedFile.type.startsWith("image/")) {
      const url = URL.createObjectURL(selectedFile)
      setPreviewUrl(url)
    } else {
      setPreviewUrl(null)
    }
  }

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!file) { toast.error("Please select a file to upload"); return }
    if (!title.trim()) { toast.error("Please enter a title for this file"); return }

    setUploading(true)
    setProgress(0)

    try {
      const folder = file.type.startsWith("image/") ? "images" : "documents"
      const storageRef = ref(storage, `${folder}/${Date.now()}-${file.name}`)
      const uploadTask = uploadBytesResumable(storageRef, file)

      await new Promise<void>((resolve, reject) => {
        uploadTask.on(
          "state_changed",
          (snapshot) => {
            const pct = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
            setProgress(pct)
          },
          (error) => reject(error),
          () => resolve()
        )
      })

      const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref)

      // Save media record to Firestore
      await addDoc(collection(db, "media"), {
        title: title.trim(),
        url: downloadUrl,
        type: file.type.startsWith("image/") ? "image" : "document",
        folder,
        createdAt: serverTimestamp(),
      })

      toast.success("Media uploaded successfully")
      closeAndReset()
      onSuccess?.()
    } catch (error: any) {
      console.error(error)
      toast.error(error.message || "Upload failed")
    } finally {
      setUploading(false)
      setProgress(0)
    }
  }

  const closeAndReset = () => {
    setFile(null)
    setTitle("")
    if (previewUrl) URL.revokeObjectURL(previewUrl)
    setPreviewUrl(null)
    setProgress(0)
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={closeAndReset}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Upload Media</DialogTitle>
          <DialogDescription>
            Upload a new image or document to your media library.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleUpload} className="space-y-6">
          {/* File Selection Area */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Select File</label>
            {!file ? (
              <div
                className="relative border-2 border-dashed border-slate-200 rounded-2xl p-8 hover:border-primary/50 hover:bg-primary/5 transition-colors group cursor-pointer"
                onClick={() => inputRef.current?.click()}
              >
                <input
                  ref={inputRef}
                  type="file"
                  className="hidden"
                  onChange={handleFileChange}
                  accept={defaultType === "image" ? "image/*" : "image/*,.pdf,.doc,.docx"}
                />
                <div className="flex flex-col items-center justify-center text-slate-500 group-hover:text-primary transition-colors">
                  <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4 group-hover:bg-primary/10 transition-colors">
                    <Upload size={28} />
                  </div>
                  <p className="font-semibold text-sm">Click to browse or drag & drop</p>
                  <p className="text-xs text-slate-400 mt-1">
                    {defaultType === "image" ? "SVG, PNG, JPG or GIF" : "Images or Documents"}
                  </p>
                </div>
              </div>
            ) : (
              <div className="relative border border-slate-200 rounded-2xl p-4 flex gap-4 items-center bg-slate-50">
                {previewUrl ? (
                  <div className="w-16 h-16 rounded-xl overflow-hidden border border-slate-200 flex-shrink-0">
                    <img src={previewUrl} alt="Preview" className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className="w-16 h-16 bg-white rounded-xl border border-slate-200 flex items-center justify-center flex-shrink-0 text-slate-400">
                    {isImage ? <ImageIcon size={24} /> : <File size={24} />}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-slate-800 truncate">{file.name}</p>
                  <p className="text-xs text-slate-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                </div>
                <button
                  type="button"
                  onClick={() => { setFile(null); setPreviewUrl(null) }}
                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <X size={18} />
                </button>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Title <span className="text-red-500">*</span>
            </label>
            <Input
              required
              placeholder="e.g. APPSC Geography Syllabus"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="h-11 bg-slate-50 border-slate-200 focus:bg-white"
            />
          </div>

          {/* Upload progress bar */}
          {uploading && (
            <div className="space-y-1">
              <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-300 rounded-full"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-xs text-slate-500 text-right">{progress}%</p>
            </div>
          )}

          <div className="pt-2 flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={closeAndReset}
              className="flex-1 h-12 rounded-xl"
              disabled={uploading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 h-12 rounded-xl bg-primary hover:bg-primary-dark font-bold text-white shadow-md"
              disabled={!file || !title || uploading}
            >
              {uploading ? (
                <>
                  <Loader2 size={18} className="animate-spin mr-2" />
                  Uploading... {progress}%
                </>
              ) : (
                "Upload File"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default UploadMediaModal
